import { VFlow } from 'bold-ui'
import { RadioOption, resolveName, SimNaoEnum } from 'components/form'
import { SimNaoSwitchButtonField } from 'components/form/field/SimNaoSwitchButtonField'
import { MChatPerguntaEnum } from 'graphql/types.generated'
import React from 'react'
import { useField } from 'react-final-form'
import { MetaPath } from 'util/metaPath'

import { ConsultaSeguimentoIndicatorHelper } from '../../components/ConsultaSeguimentoIndicatorHelper'
import { ConsultaSeguimentoIndicatorResultado } from '../../components/ConsultaSeguimentoIndicatorResultado'
import { ConsultaSeguimentoIndicatorStart } from '../../components/ConsultaSeguimentoIndicatorStart'
import { ConsultaSeguimentoPerguntaHeader } from '../../components/ConsultaSeguimentoPerguntaHeader'
import { ConsultaSeguimentoPerguntaHeaderTitle } from '../../components/ConsultaSeguimentoPerguntaHeaderTitle'
import { PerguntaSimNaoRadioListField } from '../../components/PerguntaSimNaoRadioListField'
import { useConsultaSeguimentoSetResultado } from '../../hooks/useConsultaSeguimentoSetResultado'
import { PassouFalhouEnum } from '../../model-consultaSeguimento'
import { hasAnySim } from '../../utils-consultaSeguimento'
import {
  ConsultaSeguimentoPergunta04GostaDeSubirEmEnum,
  ConsultaSeguimentoPergunta04Model,
} from './model-consultaSeguimentoPergunta04'

interface ConsultaSeguimentoPergunta04FieldProps {
  name: MetaPath<ConsultaSeguimentoPergunta04Model>
}

export function ConsultaSeguimentoPergunta04Field(props: Readonly<ConsultaSeguimentoPergunta04FieldProps>) {
  const { name } = props

  const {
    input: { value },
  } = useField<ConsultaSeguimentoPergunta04Model>(resolveName(name), {
    subscription: { value: true },
  })

  useConsultaSeguimentoSetResultado<ConsultaSeguimentoPergunta04Model>({
    name,
    conditionToCalculate,
    calculateResultado,
  })

  return (
    <VFlow>
      <ConsultaSeguimentoPerguntaHeader
        pergunta={MChatPerguntaEnum.PERGUNTA_04}
        title={<ConsultaSeguimentoPerguntaHeaderTitle sufix=' gosta de subir nas coisas?' />}
        status={value.status}
        field={<SimNaoSwitchButtonField name={name.PERGUNTA_04_HEADER} />}
      />
      {value.PERGUNTA_04_HEADER && (
        <VFlow vSpacing={0}>
          <ConsultaSeguimentoIndicatorStart />
          <VFlow>
            {value.PERGUNTA_04_HEADER === SimNaoEnum.SIM && (
              <ConsultaSeguimentoIndicatorHelper
                title='Por favor, dê um exemplo de alguma coisa na qual ele goste de subir.'
                subtitle='(Se o genitor não der um dos exemplos abaixo, perguntar cada um deles individualmente.)'
              />
            )}
            <PerguntaSimNaoRadioListField<ConsultaSeguimentoPergunta04GostaDeSubirEmEnum>
              name={name.PERGUNTA_04_GOSTA_DE_SUBIR_EM}
              label='Ele gosta de subir em...'
              options={PERGUNTA_04_GOSTA_DE_SUBIR_EM_OPTIONS}
              required
            />
          </VFlow>
          {value.resultado && (
            <ConsultaSeguimentoIndicatorResultado
              text={{
                FALHOU: 'Não para todos:',
                PASSOU: 'Sim para qualquer um:',
              }}
              resultado={value.resultado}
            />
          )}
        </VFlow>
      )}
    </VFlow>
  )
}

const PERGUNTA_04_GOSTA_DE_SUBIR_EM_OPTIONS: RadioOption<ConsultaSeguimentoPergunta04GostaDeSubirEmEnum>[] = [
  {
    label: 'Escadas?',
    value: ConsultaSeguimentoPergunta04GostaDeSubirEmEnum.ESCADAS,
  },
  {
    label: 'Cadeiras?',
    value: ConsultaSeguimentoPergunta04GostaDeSubirEmEnum.CADEIRAS,
  },
  {
    label: 'Móveis?',
    value: ConsultaSeguimentoPergunta04GostaDeSubirEmEnum.MOVEIS,
  },
  {
    label: 'Brinquedos em parques?',
    value: ConsultaSeguimentoPergunta04GostaDeSubirEmEnum.BRINQUEDOS,
  },
]

const conditionToCalculate = (values: ConsultaSeguimentoPergunta04Model): boolean => {
  return (
    hasPergunta04GostaDeSubirEmAllNao(values?.PERGUNTA_04_GOSTA_DE_SUBIR_EM) ||
    hasAnySim(values?.PERGUNTA_04_GOSTA_DE_SUBIR_EM)
  )
}

const calculateResultado = (values: ConsultaSeguimentoPergunta04Model): PassouFalhouEnum => {
  if (hasAnySim(values?.PERGUNTA_04_GOSTA_DE_SUBIR_EM)) {
    return PassouFalhouEnum.PASSOU
  }
  if (hasPergunta04GostaDeSubirEmAllNao(values?.PERGUNTA_04_GOSTA_DE_SUBIR_EM)) {
    return PassouFalhouEnum.FALHOU
  }
}

const hasPergunta04GostaDeSubirEmAllNao = (
  values: ConsultaSeguimentoPergunta04Model['PERGUNTA_04_GOSTA_DE_SUBIR_EM']
): boolean =>
  values?.ESCADAS === SimNaoEnum.NAO &&
  values?.CADEIRAS === SimNaoEnum.NAO &&
  values?.MOVEIS === SimNaoEnum.NAO &&
  values?.BRINQUEDOS === SimNaoEnum.NAO
